import { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BASE_URL = 'https://getgreenlightapp.com';

/**
 * Build the share payload for a Green Light verdict.
 * Verdict emoji is intentional — makes the share visually distinct in feeds.
 */
function verdictEmoji(decision) {
  return decision === 'GO' ? '✅' : decision === 'MAYBE' ? '🤔' : '🚫';
}

function buildScanShareText({ scanId, itemLabel, decision }) {
  const item = itemLabel || 'this product';
  const shareUrl = scanId ? `${BASE_URL}/s/${scanId}` : BASE_URL;
  const line = decision === 'GO'
    ? `Just scanned ${item} on Green Light — GO! ${verdictEmoji(decision)}`
    : decision === 'MAYBE'
    ? `Scanned ${item} on Green Light — MAYBE. ${verdictEmoji(decision)}`
    : `Dodged ${item} thanks to Green Light — HELL NO. ${verdictEmoji(decision)}`;
  return `${line}\n\nSee the verdict → ${shareUrl}`;
}

function buildMenuShareText({ items = [] }) {
  const counts = { GO: 0, MAYBE: 0, 'HELL NO': 0 };
  for (const it of items) {
    for (const v of it.verdicts || []) {
      counts[v.decision] = (counts[v.decision] || 0) + 1;
    }
  }
  const summary = `${counts.GO}×GO ✅  ${counts.MAYBE}×MAYBE 🤔  ${counts['HELL NO']}×HELL NO 🚫`;
  return `Just scanned a menu on Green Light — verdicts for the whole table:\n${summary}\n\nTry it free → ${BASE_URL}`;
}

/**
 * ShareButton — native Web Share API when available, clipboard fallback otherwise.
 *
 * Props:
 *   kind: 'scan' | 'menu'
 *   payload: { scanId?, itemLabel?, decision? } | { items? }
 *   testId?: string
 *   compact?: boolean   Small icon-only button vs full label
 */
export default function ShareButton({ kind, payload, testId, compact = false }) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const text = kind === 'menu' ? buildMenuShareText(payload) : buildScanShareText(payload);
    const title = kind === 'menu' ? 'My Green Light menu verdicts' : 'My Green Light verdict';
    const shareUrl = payload?.scanId ? `${BASE_URL}/s/${payload.scanId}` : BASE_URL;
    if (navigator.vibrate) navigator.vibrate(20);

    // Prefer native share sheet on iOS / Android — dodges the copy-paste step entirely.
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return; // user cancelled
        console.warn('[Share] navigator.share failed, falling back to clipboard:', e);
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied — paste it anywhere!');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error('[Share] clipboard write failed:', e);
      toast.error('Could not copy. Try again?');
    }
  };

  const Icon = copied ? Check : Share2;

  if (compact) {
    return (
      <button
        onClick={handleShare}
        data-testid={testId || 'share-button'}
        aria-label="Share"
        className="w-11 h-11 rounded-full bg-qc-surface border border-qc-border text-qc-text tappable flex items-center justify-center"
      >
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </button>
    );
  }

  return (
    <button
      onClick={handleShare}
      data-testid={testId || 'share-button'}
      className="tappable inline-flex items-center gap-2 h-11 px-5 rounded-full bg-qc-surface border border-qc-border text-qc-text font-bold text-sm"
    >
      <Icon className="w-4 h-4" strokeWidth={2.5} />
      {copied ? 'Copied' : 'Share'}
    </button>
  );
}
